import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  CheckCircle2,
  AlertTriangle,
  Copy,
  Check,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Key,
  ArrowRight,
  ShieldAlert,
  Camera,
  Sparkles,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";

// Meta Pixel helper — base pixel installed in /public/index.html (id 993045030025532)
function fbqTrack(event, params = {}, options = undefined) {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    try {
      if (options) {
        window.fbq("track", event, params, options);
      } else {
        window.fbq("track", event, params);
      }
    } catch (e) {
      /* tracking should never break UX */
    }
  }
}

function rupiah(n) {
  return "Rp" + Number(n || 0).toLocaleString("id-ID");
}

function CopyBtn({ value, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }}
      className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg bg-white text-red-700 border border-red-200 hover:bg-red-50 transition shrink-0"
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5" /> Tersalin
        </>
      ) : (
        <>
          <Copy className="w-3.5 h-3.5" /> Salin
        </>
      )}
    </button>
  );
}

export default function PromoThankYou() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const slug = params.get("slug") || "fomo";

  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [showPassword, setShowPassword] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const pollRef = useRef(null);
  const purchaseFiredRef = useRef(false);

  const fetchCreds = async () => {
    if (!orderId) {
      setState({ loading: false, data: null, error: "order_id tidak ditemukan di URL." });
      return;
    }
    try {
      const { data } = await api.get(`/promo/credentials/${orderId}`);
      setState({ loading: false, data, error: null });
      // Fire client-side Purchase ONLY when status=ready, exactly once, with event_id
      // matching the server-side CAPI Purchase for deduplication.
      if (data?.status === "ready" && !purchaseFiredRef.current) {
        purchaseFiredRef.current = true;
        fbqTrack(
          "Purchase",
          {
            value: Number(data.amount || 0),
            currency: "IDR",
            content_ids: [data.promo_slug || slug],
            content_name: data.promo_slug || slug,
            content_type: "product",
            contents: [{ id: data.promo_slug || slug, quantity: 1 }],
            order_id: data.order_id,
          },
          { eventID: `purchase-${data.order_id}` }
        );
      }
    } catch (e) {
      const msg = apiErr(e);
      setState({ loading: false, data: null, error: msg });
    }
  };

  useEffect(() => {
    fetchCreds();
  }, [orderId]);

  // Poll every 4s while payment is still pending (webhook may lag a few seconds
  // after the iPaymu redirect).
  useEffect(() => {
    if (state.data?.status === "pending") {
      pollRef.current = setInterval(fetchCreds, 4000);
      return () => clearInterval(pollRef.current);
    }
    if (pollRef.current) clearInterval(pollRef.current);
  }, [state.data?.status]);

  // ─────────────── Loading ───────────────
  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  // ─────────────── Error ───────────────
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center" data-testid="thankyou-error">
          <ShieldAlert className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="font-display text-2xl font-extrabold text-slate-900">
            Tidak dapat memuat kredensial
          </h1>
          <p className="text-slate-600 mt-2 text-sm">{state.error}</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-3 rounded-xl font-bold mt-6 hover:bg-red-700 transition"
          >
            Ke Halaman Login <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs text-slate-400 mt-4">
            Jika Anda yakin sudah membayar, gunakan menu &quot;Lupa Password&quot; di halaman login dengan
            email pendaftaran Anda.
          </p>
        </div>
      </div>
    );
  }

  // ─────────────── Payment still pending ───────────────
  if (state.data?.status === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-black to-slate-900 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center" data-testid="thankyou-pending">
          <Loader2 className="w-12 h-12 text-amber-500 mx-auto mb-3 animate-spin" />
          <h1 className="font-display text-2xl font-extrabold text-slate-900">
            Memverifikasi pembayaran…
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            {state.data.message ||
              "Pembayaran sedang diproses oleh iPaymu. Halaman ini akan otomatis menampilkan kredensial Anda setelah pembayaran terverifikasi (~10–60 detik)."}
          </p>
          <div className="mt-6 text-xs text-slate-400 font-mono">
            Order ID: {orderId}
          </div>
        </div>
      </div>
    );
  }

  // ─────────────── Ready: show credentials ───────────────
  const { email, password, name, plan, amount } = state.data || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50">
      {/* Hero */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-4 animate-pulse">
            <CheckCircle2 className="w-12 h-12 text-emerald-600" strokeWidth={2.5} />
          </div>
          <p className="text-emerald-700 font-bold uppercase tracking-widest text-xs">
            Pembayaran Berhasil
          </p>
          <h1 className="font-display text-3xl sm:text-5xl font-black text-slate-900 mt-2 leading-tight">
            Selamat{name ? `, ${name.split(" ")[0]}` : ""}! 🎉
            <br />
            Akun <span className="text-emerald-600">Agency</span> kamu sudah aktif.
          </h1>
          <p className="text-slate-600 mt-4 text-base sm:text-lg">
            Total dibayar: <b>{rupiah(amount)}</b> · Plan:{" "}
            <b className="uppercase">{plan || "agency"}</b> · 8.000 kredit + akses penuh selama 365
            hari.
          </p>
        </div>
      </section>

      {/* ⚠️ MAIN ALERT */}
      <section className="px-4">
        <div className="max-w-2xl mx-auto">
          <div
            className="bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-2xl p-5 sm:p-6 shadow-xl border-4 border-red-300 animate-shake"
            data-testid="save-credentials-alert"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-7 h-7 shrink-0 mt-0.5" strokeWidth={2.5} />
              <div>
                <p className="font-display text-xl sm:text-2xl font-black">
                  ⚠️ SIMPAN SEKARANG JUGA — KAMI TIDAK MENGIRIM EMAIL.
                </p>
                <p className="text-red-50 text-sm sm:text-base mt-2 leading-relaxed">
                  Ini <b>satu-satunya kesempatan</b> untuk melihat password kamu. Kalau halaman ini
                  ditutup tanpa menyimpan, kamu harus reset via &quot;Lupa Password&quot; di halaman login.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4 text-xs sm:text-sm font-semibold">
                  <div className="bg-white/10 rounded-lg p-2 flex items-center gap-2">
                    <Camera className="w-4 h-4" /> Screenshot halaman ini
                  </div>
                  <div className="bg-white/10 rounded-lg p-2 flex items-center gap-2">
                    <Copy className="w-4 h-4" /> Salin ke catatan / password manager
                  </div>
                  <div className="bg-white/10 rounded-lg p-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> Ganti di Profil setelah login
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials card */}
      <section className="px-4 py-8">
        <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl border border-slate-100 p-6 sm:p-8">
          <p className="font-display text-lg font-extrabold text-slate-900 mb-5 flex items-center gap-2">
            <Key className="w-5 h-5 text-red-600" /> Kredensial Login Kamu
          </p>

          {/* Email */}
          <div className="border border-slate-200 rounded-xl p-4 mb-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Email / Username
            </p>
            <div className="flex items-center gap-3 mt-2">
              <p
                className="flex-1 font-mono text-sm sm:text-base font-bold text-slate-900 break-all"
                data-testid="cred-email"
              >
                {email}
              </p>
              <CopyBtn value={email} testid="copy-email-btn" />
            </div>
          </div>

          {/* Password */}
          <div className="border-2 border-red-200 bg-red-50/30 rounded-xl p-4">
            <p className="text-xs font-semibold text-red-600 uppercase tracking-wide flex items-center gap-1.5">
              <Key className="w-3.5 h-3.5" /> Password (auto-generated)
            </p>
            <div className="flex items-center gap-3 mt-2">
              <p
                className="flex-1 font-mono text-base sm:text-lg font-black text-red-700 tracking-wider"
                data-testid="cred-password"
              >
                {showPassword ? password : "•".repeat(password?.length || 12)}
              </p>
              <button
                type="button"
                data-testid="toggle-password-btn"
                onClick={() => setShowPassword((s) => !s)}
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 transition shrink-0"
              >
                {showPassword ? (
                  <>
                    <EyeOff className="w-3.5 h-3.5" /> Sembunyikan
                  </>
                ) : (
                  <>
                    <Eye className="w-3.5 h-3.5" /> Lihat
                  </>
                )}
              </button>
              <CopyBtn value={password} testid="copy-password-btn" />
            </div>
          </div>

          {/* Guide */}
          <div className="mt-6 bg-slate-50 rounded-xl p-4 border border-slate-200">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
              Cara login (3 langkah)
            </p>
            <ol className="space-y-2 text-sm text-slate-700 list-decimal pl-5">
              <li>
                Salin <b>email</b> dan <b>password</b> di atas, lalu simpan di password manager.
              </li>
              <li>
                Buka{" "}
                <Link to="/login" className="text-red-600 font-bold hover:underline">
                  halaman Login
                </Link>{" "}
                → paste email + password → klik <b>Continue</b>.
              </li>
              <li>
                Setelah masuk dashboard, buka <b>Profil</b> dan ganti password ke yang mudah kamu
                ingat. (Disarankan dalam 24 jam.)
              </li>
            </ol>
          </div>

          {/* Acknowledge + CTA */}
          <div className="mt-6 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                data-testid="ack-checkbox"
                className="mt-1 w-5 h-5 accent-red-600 cursor-pointer"
              />
              <span className="text-sm text-slate-700">
                Saya sudah <b>menyalin / screenshot</b> email dan password di atas, dan paham bahwa
                halaman ini <b>tidak akan dikirim ulang via email</b>.
              </span>
            </label>

            <Link
              to="/login"
              onClick={(e) => {
                if (!acknowledged) {
                  e.preventDefault();
                  // brief visual nudge
                  const el = e.currentTarget;
                  el.classList.add("animate-shake");
                  setTimeout(() => el.classList.remove("animate-shake"), 500);
                }
              }}
              aria-disabled={!acknowledged}
              data-testid="go-to-login-btn"
              className={`inline-flex w-full items-center justify-center gap-2 px-6 py-4 rounded-xl font-black uppercase tracking-wide text-lg transition ${
                acknowledged
                  ? "bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-200"
                  : "bg-slate-200 text-slate-400 cursor-not-allowed"
              }`}
            >
              {acknowledged ? "Sudah Disimpan — Buka Login" : "Centang dulu untuk lanjut"}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center text-xs text-slate-400 py-8">
        <p>
          Butuh bantuan? Hubungi{" "}
          <a href="mailto:support@resada.id" className="text-red-600 font-bold hover:underline">
            support@resada.id
          </a>
        </p>
        <p className="mt-1 font-mono text-[10px] text-slate-300">Order ID: {orderId}</p>
      </footer>
    </div>
  );
}
